import type { AbbreviationsSliceData } from "./slices/abbreviations";
import type { Activity } from "./slices/activities";
import type { Application } from "./slices/applications";
import type { AuthSliceData } from "./slices/auth";
import type { CaseListMatter } from "./slices/caseList";
import { EMPTY_ENTRY_FILTERS, fetchEntryFilters } from "./slices/entryFilters";
import type { Matter } from "./slices/matters";
import type {
	ReportingEntriesData,
	ReportingEntry,
} from "./slices/reportingEntries";
import type { ReportsData } from "./slices/reports";
import type { RestrictedWordRule } from "./slices/restrictedWords";
import type { Settings } from "./slices/settings.types";
import { defaultSettings, parseSettings } from "./slices/settings.types";
import type { Task } from "./slices/tasks";
import type { CacheSliceConfig } from "./types";

const defaultAuthData: AuthSliceData = {
	userId: "",
	email: "",
	firstName: null,
	lastName: null,
	displayName: "",
	initials: "?",
	isAdmin: false,
	adminPermissions: {
		overview: false,
		billing: false,
		entries: false,
		calendar: false,
		support: false,
	},
};

export const cacheSlices = {
	auth: {
		key: "auth",
		fetch: async () => defaultAuthData as AuthSliceData,
		initialValue: defaultAuthData as AuthSliceData,
	},

	abbreviations: {
		key: "abbreviations",
		fetch: async (api) => {
			const res = await api.makeApiRequest({
				endpoint: "/abbreviations",
				method: "GET",
			});
			const data = res.data ?? { enabled: true, abbreviations: [] };
			return {
				enabled: data.enabled,
				abbreviations: (data.abbreviations ?? []).map(
					(a: { id: number; abbreviation: string; expansion: string }) => ({
						id: a.id,
						abbreviation: a.abbreviation,
						expansion: a.expansion,
					}),
				),
			} as AbbreviationsSliceData;
		},
		initialValue: {
			enabled: true,
			abbreviations: [],
		} as AbbreviationsSliceData,
		persistKey: "cache:abbreviations",
	},

	restricted_words: {
		key: "restricted_words",
		fetch: async (api) => {
			const res = await api.makeApiRequest({
				endpoint: "/records/restrictedWords?all=true",
				method: "GET",
			});
			return (res.data ?? []) as RestrictedWordRule[];
		},
		initialValue: [] as RestrictedWordRule[],
		persistKey: "cache:restricted_words",
	},

	settings: {
		key: "settings",
		fetch: async (api) => {
			const res = await api.makeApiRequest({
				endpoint: "/settings",
				method: "GET",
			});
			return parseSettings(res.data?.[0]);
		},
		initialValue: defaultSettings as Settings,
		persistKey: "cache:settings",
	},

	matters: {
		key: "matters",
		fetch: async (api) => {
			const res = await api.makeApiRequest({
				endpoint: "/matters",
				method: "GET",
			});
			return (res.data ?? []) as Matter[];
		},
		initialValue: [] as Matter[],
		persistKey: "cache:matters",
	},

	case_list: {
		key: "case_list",
		fetch: async (api) => {
			const res = await api.makeApiRequest({
				endpoint: "/matters/case-list",
				method: "GET",
			});
			return (res.data ?? []) as CaseListMatter[];
		},
		initialValue: [] as CaseListMatter[],
		persistKey: "cache:case_list",
	},

	tasks: {
		key: "tasks",
		fetch: async (api) => {
			const res = await api.makeApiRequest({
				endpoint: "/tasks/allTasks",
				method: "GET",
			});
			return (res.data ?? []) as Task[];
		},
		initialValue: [] as Task[],
	},

	activities: {
		key: "activities",
		fetch: async (api) => {
			const res = await api.makeApiRequest({
				endpoint: "/activities",
				method: "GET",
			});
			return (res.data ?? []) as Activity[];
		},
		initialValue: [] as Activity[],
	},
	applications: {
		key: "applications",
		fetch: async (api) => {
			const res = await api.makeApiRequest({
				endpoint: "/applications",
				method: "GET",
			});
			return (res.data ?? []) as Application[];
		},
		initialValue: [] as Application[],
		persistKey: "cache:applications",
	},
	reports: {
		key: "reports",
		fetch: async (api) => {
			const res = await api.makeApiRequest({
				endpoint: "/user/reports",
				method: "GET",
			});
			return (res.data ?? { managers: [], manages: [] }) as ReportsData;
		},
		initialValue: { managers: [], manages: [] } as ReportsData,
		persistKey: "cache:reports",
	},
	reporting_entries: {
		key: "reporting_entries",
		fetch: async (api) => {
			const res = await api.makeApiRequest({
				endpoint: "/team/entries",
				method: "GET",
			});
			return {
				entries: (res.data?.entries ?? []) as ReportingEntry[],
				userProfId: (res.data?.userProfId ?? "") as string,
			} as ReportingEntriesData;
		},
		initialValue: { entries: [], userProfId: "" } as ReportingEntriesData,
		persistKey: "cache:reporting_entries",
	},
	entry_filters: {
		key: "entry_filters",
		fetch: async (api) => {
			return await fetchEntryFilters(api);
		},
		initialValue: EMPTY_ENTRY_FILTERS,
		persistKey: "cache:entry_filters",
	},
} satisfies Record<string, CacheSliceConfig<unknown>>;

export type CacheSliceKey = keyof typeof cacheSlices;
export type CacheSliceData<K extends CacheSliceKey> = Awaited<
	ReturnType<(typeof cacheSlices)[K]["fetch"]>
>;
